import React, { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE; // ✅ Unified API root

const AdminSupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);

  // 🔹 Fetch all support tickets
  const loadTickets = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/support/tickets`);
      setTickets(res.data || []);
    } catch (err) {
      console.error("❌ Failed to fetch tickets:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, []);

  // 🔹 Update ticket (reply or close)
  const updateTicket = async (id, status) => {
    if (!response.trim() && status !== "closed") {
      alert("Please write a reply before resolving.");
      return;
    }

    try {
      await axios.post(`${API_BASE}/support/tickets/update`, {
        id,
        status,
        response: response || selected?.response,
      });

      alert("✅ Ticket updated successfully!");
      setResponse("");
      setSelected(null);
      loadTickets();
    } catch (err) {
      console.error("❌ Failed to update ticket:", err);
      alert("❌ Failed to update ticket.");
    }
  };

  return (
    <div className="ml-64 p-6 min-h-screen bg-neutral-50">
      <h1 className="text-3xl font-bold mb-6">📩 Support Tickets</h1>

      <div className="grid grid-cols-4 gap-6">
        {/* ================= LEFT: Ticket List ================= */}
        <div className="col-span-1 bg-white rounded-xl border border-neutral-200 shadow p-4 overflow-y-auto max-h-[80vh]">
          <h2 className="font-semibold mb-3">All Tickets</h2>

          {loading ? (
            <p className="text-sm text-neutral-500">Loading tickets...</p>
          ) : tickets.length === 0 ? (
            <p className="text-sm text-neutral-500">No tickets found.</p>
          ) : (
            tickets.map((t) => (
              <button
                key={t._id}
                onClick={() => setSelected(t)}
                className={`w-full text-left px-3 py-2 rounded-lg border mb-2 transition ${
                  selected?._id === t._id
                    ? "bg-neutral-900 text-white border-neutral-900"
                    : "bg-white hover:bg-neutral-100 border-neutral-200 text-neutral-800"
                }`}
              >
                <p className="font-medium text-sm truncate">{t.subject}</p>
                <p className="text-xs text-neutral-400">{t.email}</p>
                <span
                  className={`text-[10px] px-2 py-0.5 rounded-full mt-1 inline-block ${
                    t.status === "resolved"
                      ? "bg-green-100 text-green-700"
                      : t.status === "closed"
                      ? "bg-red-100 text-red-700"
                      : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  {t.status || "open"}
                </span>
              </button>
            ))
          )}
        </div>

        {/* ================= RIGHT: Ticket Detail ================= */}
        <div className="col-span-3 bg-white rounded-xl border border-neutral-200 shadow p-6">
          {selected ? (
            <>
              <h2 className="text-xl font-semibold mb-2">{selected.subject}</h2>
              <p className="text-sm text-neutral-600 mb-4">
                From: {selected.name || "User"} ({selected.email})
              </p>

              <div className="border border-neutral-200 rounded-lg p-3 bg-neutral-50 mb-3">
                <p className="text-sm">{selected.message}</p>
              </div>

              {selected.response && (
                <div className="border-l-4 border-emerald-500 bg-emerald-50 p-3 rounded mb-3">
                  <strong>Support:</strong> {selected.response}
                </div>
              )}

              <textarea
                rows="3"
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                placeholder="Write a reply..."
                className="w-full border border-neutral-300 rounded-lg p-2 mb-3 focus:ring-2 focus:ring-neutral-900"
              ></textarea>

              <div className="flex gap-2">
                <button
                  onClick={() => updateTicket(selected._id, "resolved")}
                  className="bg-neutral-900 text-white px-4 py-2 rounded-lg hover:opacity-90"
                >
                  Send Reply & Resolve
                </button>
                <button
                  onClick={() => updateTicket(selected._id, "closed")}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                >
                  Close Ticket
                </button>
              </div>
            </>
          ) : (
            <p className="text-neutral-500 text-center m-auto">
              Select a ticket to view details.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSupportTickets;
